import { useState, useMemo } from 'react';
import { Card, CardHeader, CardContent } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Modal } from '@/components/ui/Modal';
import { useToast } from '@/components/ui/Toast';
import {
  useNoteCategories,
  useCreateNoteCategory,
  useUpdateNoteCategory,
  useDeleteNoteCategory,
  type NoteCategory,
  type CreateNoteCategoryPayload,
  type UpdateNoteCategoryPayload,
} from '@/features/contacts/hooks/useNoteCategories';
import { usePermissions, PERMISSIONS } from '@/hooks/usePermissions';

/**
 * NoteCategoriesPage — manage note categories used for categorizing contact notes.
 * Categories support color coding, RBAC via allowed_roles, and scheduling flags.
 */
export function NoteCategoriesPage() {
  const { can } = usePermissions();
  const { toast } = useToast();
  const { data: categories, isLoading } = useNoteCategories({ includeInactive: true });
  const deleteMutation = useDeleteNoteCategory();
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<NoteCategory | null>(null);

  const sortedCategories = useMemo(
    () => [...(categories ?? [])].sort((a, b) => a.displayOrder - b.displayOrder),
    [categories],
  );

  const activeCount = sortedCategories.filter((c) => c.isActive).length;

  const handleCreate = () => {
    setEditingCategory(null);
    setIsEditorOpen(true);
  };

  const handleEdit = (category: NoteCategory) => {
    setEditingCategory(category);
    setIsEditorOpen(true);
  };

  const handleDelete = (category: NoteCategory) => {
    if (
      !confirm(
        `Delete category "${category.name}"? Notes using this category will retain their label.`,
      )
    ) {
      return;
    }
    deleteMutation.mutate(category.id, {
      onSuccess: () => toast({ variant: 'success', title: `"${category.name}" deleted` }),
      onError: () => toast({ variant: 'error', title: 'Failed to delete category' }),
    });
  };

  const handleEditorClose = () => {
    setIsEditorOpen(false);
    setEditingCategory(null);
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader
          title="Note Categories"
          description={`Manage categories for contact notes. ${activeCount} active of ${sortedCategories.length} total.`}
          action={
            can(PERMISSIONS.NOTE_CATEGORIES_CREATE) ? (
              <Button size="sm" onClick={handleCreate}>
                Add Category
              </Button>
            ) : undefined
          }
        />
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-brand-600 border-t-transparent" />
                Loading categories...
              </div>
            </div>
          ) : sortedCategories.length === 0 ? (
            <p className="py-8 text-center text-sm text-gray-500 dark:text-gray-400">
              No note categories configured. Click &quot;Add Category&quot; to create one.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:border-border-dark dark:text-gray-400">
                    <th className="px-3 py-2">Color</th>
                    <th className="px-3 py-2">Name</th>
                    <th className="px-3 py-2">Description</th>
                    <th className="px-3 py-2">Scheduling</th>
                    <th className="px-3 py-2">Status</th>
                    <th className="px-3 py-2">Order</th>
                    <th className="px-3 py-2 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border dark:divide-border-dark">
                  {sortedCategories.map((category) => (
                    <tr
                      key={category.id}
                      className="hover:bg-surface-secondary dark:hover:bg-surface-dark-tertiary transition-colors"
                    >
                      <td className="px-3 py-2.5">
                        {category.color ? (
                          <span
                            className="inline-block h-5 w-5 rounded-full border border-gray-200 dark:border-gray-600"
                            style={{ backgroundColor: category.color }}
                            title={category.color}
                          />
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </td>
                      <td className="px-3 py-2.5 font-medium text-gray-900 dark:text-gray-100">
                        {category.name}
                      </td>
                      <td className="px-3 py-2.5 text-gray-500 dark:text-gray-400">
                        {category.description || '—'}
                      </td>
                      <td className="px-3 py-2.5">
                        {category.isScheduling ? (
                          <Badge variant="info" size="sm">
                            Yes
                          </Badge>
                        ) : (
                          <span className="text-gray-400">No</span>
                        )}
                      </td>
                      <td className="px-3 py-2.5">
                        <Badge variant={category.isActive ? 'success' : 'default'} size="sm">
                          {category.isActive ? 'Active' : 'Inactive'}
                        </Badge>
                      </td>
                      <td className="px-3 py-2.5 text-gray-500">{category.displayOrder}</td>
                      <td className="px-3 py-2.5 text-right">
                        <div className="flex items-center justify-end gap-1">
                          {can(PERMISSIONS.NOTE_CATEGORIES_UPDATE) && (
                            <Button size="sm" variant="ghost" onClick={() => handleEdit(category)}>
                              Edit
                            </Button>
                          )}
                          {can(PERMISSIONS.NOTE_CATEGORIES_DELETE) && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleDelete(category)}
                              className="text-danger-600 hover:text-danger-700"
                            >
                              Delete
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Editor Modal */}
      {isEditorOpen && (
        <CategoryEditorModal category={editingCategory} onClose={handleEditorClose} />
      )}
    </div>
  );
}

// ─── Editor Modal ─────────────────────────────────────────────────────────────

function CategoryEditorModal({
  category,
  onClose,
}: {
  category: NoteCategory | null;
  onClose: () => void;
}) {
  const { toast } = useToast();
  const isEditing = !!category;

  const createMutation = useCreateNoteCategory();
  const updateMutation = useUpdateNoteCategory(category?.id ?? '');

  const [name, setName] = useState(category?.name ?? '');
  const [description, setDescription] = useState(category?.description ?? '');
  const [color, setColor] = useState(category?.color ?? '#6B7280');
  const [isActive, setIsActive] = useState(category?.isActive ?? true);
  const [isScheduling, setIsScheduling] = useState(category?.isScheduling ?? false);
  const [displayOrder, setDisplayOrder] = useState(category?.displayOrder ?? 0);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!name.trim()) {
      toast({ variant: 'error', title: 'Name is required' });
      return;
    }

    if (isEditing) {
      const payload: UpdateNoteCategoryPayload = {
        name: name.trim(),
        description: description.trim() || null,
        color: color || null,
        is_active: isActive,
        is_scheduling: isScheduling,
        display_order: displayOrder,
      };

      updateMutation.mutate(payload, {
        onSuccess: () => {
          toast({ variant: 'success', title: `"${name.trim()}" updated` });
          onClose();
        },
        onError: () => toast({ variant: 'error', title: 'Failed to update category' }),
      });
    } else {
      const payload: CreateNoteCategoryPayload = {
        name: name.trim(),
        description: description.trim() || null,
        color: color || null,
        is_active: isActive,
        is_scheduling: isScheduling,
        display_order: displayOrder,
      };

      createMutation.mutate(payload, {
        onSuccess: () => {
          toast({ variant: 'success', title: `"${name.trim()}" created` });
          onClose();
        },
        onError: () => toast({ variant: 'error', title: 'Failed to create category' }),
      });
    }
  };

  const isPending = createMutation.isPending || updateMutation.isPending;

  return (
    <Modal open onClose={onClose} title={isEditing ? 'Edit Note Category' : 'New Note Category'}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <Input
          label="Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
          placeholder="e.g., Phone Call"
          maxLength={100}
        />

        <Input
          label="Description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Brief description (optional)"
          maxLength={250}
        />

        <div>
          <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
            Color
          </label>
          <div className="flex items-center gap-3">
            <input
              type="color"
              value={color}
              onChange={(e) => setColor(e.target.value)}
              className="h-9 w-12 cursor-pointer rounded border border-border dark:border-border-dark"
            />
            <Input
              value={color}
              onChange={(e) => setColor(e.target.value)}
              placeholder="#6B7280"
              className="flex-1"
            />
          </div>
        </div>

        <Input
          label="Display Order"
          type="number"
          value={String(displayOrder)}
          onChange={(e) => setDisplayOrder(parseInt(e.target.value, 10) || 0)}
          min={0}
        />

        <div className="flex items-center gap-6">
          <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
            <input
              type="checkbox"
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 text-brand-600"
            />
            Active
          </label>

          <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
            <input
              type="checkbox"
              checked={isScheduling}
              onChange={(e) => setIsScheduling(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 text-brand-600"
            />
            Scheduling (auto-creates reminder)
          </label>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" loading={isPending}>
            {isEditing ? 'Save Changes' : 'Create'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
